import * as React from 'react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogBodyProps } from '@fluentui/react-dialog'
import useAxios from '../../../../hooks/useAxios'
import { Button, DialogActions, DialogBody, DialogContent, DialogTitle, DialogTrigger, makeStyles, MessageBar } from '@fluentui/react-components'
import FormContainer from '../../../../components/Form/FormContainer'
import { HammockDialogProps } from '../HammockDialog'
import { Destination } from '../../../../context/AxiosContext'
import { AxiosError } from 'axios'
import { FolderDriveItem } from '../../../../components/Document/DocumentFlatTree'
import { tokens } from '@fluentui/react-theme'

const useStyles = makeStyles({
    deleteButton: {
        backgroundColor: tokens.colorPaletteRedForeground3,
        '&:hover, &:active': {
            backgroundColor: `${tokens.colorPaletteRedForeground1} !important`,
        },
        '&:hover:active': {
            backgroundColor: `${tokens.colorPaletteRedForeground2} !important`,
        },
    },
})

export type DeleteDocumentDialogBodyProps = DialogBodyProps & {
    items: FolderDriveItem[]
    callback?: (driveItems: FolderDriveItem[]) => void
}

export default function DeleteDocumentDialogBody(props: HammockDialogProps<DeleteDocumentDialogBodyProps>) {
    const axios = useAxios()
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const dialogBodyProps = useMemo(() => props.dialogBodyProps?.() ?? ({} as DeleteDocumentDialogBodyProps), [props])

    const [error, setError] = useState<string>()
    const title = translate('file.pivot.document.delete.title', { count: dialogBodyProps.items.length })
    const subTitle = translate('file.pivot.document.delete.subtitle', { count: dialogBodyProps.items.length })

    return (
        <DialogBody>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <FormContainer>
                    {error && (
                        <MessageBar intent={'error'} layout={'multiline'}>
                            {error}
                        </MessageBar>
                    )}
                    <div>{subTitle}</div>
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <Button
                    appearance="primary"
                    className={styles.deleteButton}
                    onClick={() => {
                        return Promise.all(
                            dialogBodyProps.items.map(item =>
                                axios.delete(`/items/${item.id}`, {
                                    baseURL: Destination.Documents,
                                    headers: { SuppressErrors: 'true' },
                                }),
                            ),
                        )
                            .then(() => {
                                props.onDismiss()
                                dialogBodyProps?.callback?.(dialogBodyProps.items)
                            })
                            .catch((error: AxiosError) => {
                                setError(error.message)
                            })
                    }}
                >
                    {translate('file.pivot.document.delete.button')}
                </Button>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">{translate('action.cancel')}</Button>
                </DialogTrigger>
            </DialogActions>
        </DialogBody>
    )
}
